/* eslint-disable @next/next/no-sync-scripts */

import { useMemo, useState } from 'react'

import type { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import { LOCALE } from '@hhgtech/hhg-components/types'
import dynamic from 'next/dynamic'

import { getServerSideProps as PageGetServerSideProps } from 'components/pages/spotlight/getServerSideProps'
import {
  PostPageTrackingScript,
  PrePageTrackingScript,
} from 'components/pages/spotlight/specialTrackingScripts'
import { NavigatorLayout } from 'components/templates/navigatorLayout'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { LOCALE_SPECS } from 'interfaces/constants'
import type {
  Article,
  FooterInfo,
  HealthTool,
  Spotlight,
  // SpotlightEmbeddingDoctorsSession,
  SpotlightTemplate,
} from 'interfaces/types'
import { ArticleStoreProvider } from 'state/article'

const SpotlightDefaultTemplate = dynamic(
  () => import('components/templates/spotlight/default'),
)
const SpotlightLandingTemplate = dynamic(
  () => import('components/templates/spotlight/landing'),
)
const SpotlightSuperContentTemplate = dynamic(
  () => import('components/templates/spotlight/superContent'),
)
const SpotlightCarePathTemplate = dynamic(
  () => import('components/templates/spotlight/carePath'),
)

export type Props = {
  spotlight: Spotlight
  footerInfo?: FooterInfo
  template: SpotlightTemplate
  dataNavMenuContent: NavMenuContentType
  pageType?: 'detail' | null
}

const LandingPage = ({
  spotlight,
  footerInfo,
  template,
  dataNavMenuContent,
  pageType,
}: Props) => {
  const isMobile = useIsMobile()
  const {
    locale,
    query: { slug },
  } = useRouterExtend()

  const [, setIsTopBarAppears] = useState(false)

  const onTopBarAppears = (b: boolean) => {
    setIsTopBarAppears(b)
  }

  const healthTools = (dataNavMenuContent?.healthTools?.items ||
    []) as HealthTool[]

  const hasSpecialTracking =
    locale === LOCALE.Thailand &&
    spotlight?.permalink?.includes(
      '/%e0%b8%a7%e0%b8%b1%e0%b8%84%e0%b8%8b%e0%b8%b5%e0%b8%99-hpv/',
    )

  const content = useMemo(
    () => (
      <SpotlightCarePathTemplate
        isMobile={isMobile}
        spotlight={spotlight}
        healthTools={healthTools}
        pageType={pageType || null}
        footerInfo={footerInfo}
      />
    ),
    [slug],
  )

  return (
    <>
      {hasSpecialTracking && <PrePageTrackingScript />}
      {template === 'services-connection' ? (
        content
      ) : (
        <NavigatorLayout
          title={spotlight.postTitle}
          description={spotlight.postTitle}
          image={LOCALE_SPECS[locale].HOMEPAGE_IMAGE}
          onTopBarAppears={onTopBarAppears}
          isSponsored={spotlight?.isSponsor}
          sponsoredType="content"
          sponsoredBy={spotlight.sponsor}
          // isHomePage={false}
          SEOData={{
            title: spotlight.postTitle,
            description: spotlight.postTitle,
            apiMetaData: spotlight.metaTag,
            isIndexable: true,
          }}
          type="profile"
          footerInfo={footerInfo}
          languageOptions={spotlight?.translateOptions || []}
          dataNavMenuContent={dataNavMenuContent}
        >
          <ArticleStoreProvider
            init={{
              currentArticle: (spotlight as unknown) as Article,
            }}
          >
            {template === 'default' && (
              <SpotlightDefaultTemplate
                isMobile={isMobile}
                spotlight={spotlight}
                healthTools={healthTools}
              />
            )}
            {template === 'super_content' && (
              <SpotlightSuperContentTemplate
                isMobile={isMobile}
                spotlight={spotlight}
                isSponsored={spotlight?.isSponsor || false}
              />
            )}
            {template === 'landing' && (
              <SpotlightLandingTemplate
                isMobile={isMobile}
                spotlight={spotlight}
                healthTools={healthTools}
              />
            )}
          </ArticleStoreProvider>
        </NavigatorLayout>
      )}

      {hasSpecialTracking && <PostPageTrackingScript />}
    </>
  )
}

export const getServerSideProps = PageGetServerSideProps

export default LandingPage
